import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { authStore } from '../stores/authStore';

class ApiService {
  private client: AxiosInstance;
  private isHandling401 = false;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 60000, // Increased to 60 seconds for OpenAI processing
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        try {
          const session = await fetchAuthSession();
          if (session.tokens?.idToken) {
            config.headers.Authorization = `Bearer ${session.tokens.idToken.toString()}`;
          }

          // Add impersonation header if root user is impersonating
          if (authStore.isImpersonating && authStore.impersonatingTenantId) {
            config.headers['X-Impersonate-Tenant'] = authStore.impersonatingTenantId;
          }
        } catch (error) {
          console.error('Failed to get auth token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !this.isHandling401) {
          // Prevent multiple simultaneous 401 handling
          this.isHandling401 = true;
          
          try {
            // Clear auth state and redirect to login
            await authStore.logout();
            window.location.href = '/login';
          } catch (logoutError) {
            console.error('Failed to logout:', logoutError);
            window.location.href = '/login';
          } finally {
            // Reset flag after a delay to allow navigation to complete
            setTimeout(() => {
              this.isHandling401 = false;
            }, 1000);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig) {
    try {
      const response = await this.client.get<T>(url, config);
      return response.data;
    } catch (error: any) {
      // If the error response has data with an error message, throw a better error
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Request failed');
      }
    }
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    try {
      const response = await this.client.post<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      // If the error response has data with an error message, throw a better error
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Request failed');
      }
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    try {
      const response = await this.client.put<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      // If the error response has data with an error message, throw a better error
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Request failed');
      }
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig) {
    try {
      const response = await this.client.delete<T>(url, config);
      return response.data;
    } catch (error: any) {
      // If the error response has data with an error message, throw a better error
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Request failed');
      }
    }
  }
}

export const apiService = new ApiService();